const MAX_DETAIL_LENGTH = 300

/**
 * @typedef {object} MiddlewareResponseDetails
 * @property {string} endpoint
 * @property {number} status
 * @property {string} statusText
 * @property {string} message - the middleware's error message, if any
 * @property {string} code - the middleware's error code, if any
 */

/**
 * A non-ok middleware response, carrying the status and the middleware's own
 * error message so captureMiddlewareError can report them
 */
export class MiddlewareResponseError extends Error {
  /**
   * @param {string} message
   * @param {MiddlewareResponseDetails} details
   */
  constructor(message, details) {
    super(message)
    this.name = 'MiddlewareResponseError'
    this.details = details
  }
}

/**
 * @param {unknown} value
 * @returns {string}
 */
const toDetail = (value) => {
  if (value === undefined || value === null || value === '') return ''
  const text = typeof value === 'string' ? value : JSON.stringify(value)
  return text.slice(0, MAX_DETAIL_LENGTH)
}

/**
 * Reads a field off a parsed JSON value without trusting its shape
 * @param {unknown} value
 * @param {string} key
 * @returns {unknown}
 */
const getField = (value, key) =>
  typeof value === 'object' && value !== null
    ? /** @type {Record<string, unknown>} */ (value)[key]
    : undefined

/**
 * Reads what the middleware said about a failed request. Only the error fields
 * of a JSON body are kept (never the whole body, which may echo the submitted
 * form), the title of an HTML error page, or the start of a plain text body.
 *
 * @param {Response} response
 * @returns {Promise<{ message: string, code: string }>}
 */
const readErrorDetail = async (response) => {
  try {
    const text = (await response.text()).trim()

    if (text.startsWith('{')) {
      /** @type {unknown} */
      const parsed = JSON.parse(text)
      // some endpoints wrap their payload in { original: {...} }
      const data = getField(parsed, 'original') || parsed
      const error = getField(data, 'error')
      return {
        message: toDetail(
          getField(data, 'message') || getField(error, 'message') || error,
        ),
        code: toDetail(getField(data, 'code') || getField(error, 'code')),
      }
    }

    if (text.startsWith('<')) {
      const title = text.match(/<title[^>]*>([^<]*)<\/title>/i)?.[1]
      return { message: toDetail(title?.trim()), code: '' }
    }

    return { message: toDetail(text), code: '' }
  } catch {
    return { message: '', code: '' }
  }
}

/**
 * Builds the error for a non-ok middleware response
 *
 * @param {Response} response - the non-ok response
 * @param {string} endpoint - e.g. '/api/form-submit'
 * @returns {Promise<MiddlewareResponseError>}
 */
export async function createMiddlewareResponseError(response, endpoint) {
  const status = response.status || 0
  const statusText = response.statusText || ''
  const { message, code } = await readErrorDetail(response)

  return new MiddlewareResponseError(
    `${endpoint} failed: ${status || 'unknown status'}${
      message ? ` - ${message}` : statusText ? ` ${statusText}` : ''
    }`,
    { endpoint, status, statusText, message, code },
  )
}
